import { useEffect, useMemo, useRef, useState } from 'react';
import { substituteResolvedValues } from '@inference.engine/taxonomy/web';
import { Trans, useTranslation } from 'react-i18next';
import { Badge, Button } from '@monksflow/monks-ui';
import { arrayMove } from '@dnd-kit/sortable';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import {
  taxonomiesQueryOptions,
  type TaxonomyField,
  type TaxonomyFieldGroup,
  type TaxonomyFieldValue,
} from '@/entities/taxonomy';
import { AddFieldModal } from '../AddFieldModal/AddFieldModal';
import { EditFieldModal } from '../EditFieldModal/EditFieldModal';
import { TrackingCodeComposer } from '../TrackingCodeComposer/TrackingCodeComposer';
import { LinkedSchemaComposer } from '../LinkedSchemaComposer/LinkedSchemaComposer';
import { LinkListModal } from '../LinkListModal/LinkListModal';
import type { ResolvedList } from '@/entities/taxonomy-list';
import { applyFieldPatch } from '../../lib/applyFieldPatch';
import type { SchemaProblem } from '../../lib/validateSchema';
import { getStandardFieldByKey } from '../FieldLibraryModal/standard-fields';
import { sampleTrackingCode } from '../../lib/trackingPattern';
import { EMPTY_VALUE, pruneEmptyRows } from '../../lib/pruneEmptyRows';
import { FormDialog } from '@/shared/ui/FormDialog';
import { GroupTable } from './GroupTable';
import { FocusTarget, GROUP_META, IMMEDIATE, MIN_GROUP_WIDTH } from './fieldsEditorShared';

export type { FocusTarget } from './fieldsEditorShared';

interface FieldsEditorProps {
  fields: TaxonomyField[];
  readOnly: boolean;
  /** `immediate` marks a batch the user made in one gesture, which is checked without waiting. */
  onChange: (next: TaxonomyField[], opts?: { immediate?: boolean }) => void;
  /** Whether a cell editor or a column rename is open anywhere in the grid. */
  onEditingChange?: (open: boolean) => void;
  /** Current schema id, so a linked column cannot target its own schema. */
  taxonomyId: string;
  /** The schema is shared across the organization, so only organization lists can be bound. */
  orgWide?: boolean;
  /**
   * Vocabularies resolved from shared lists, keyed by field id. Rendered for
   * list-bound columns; `field.values` remains the authoritative persisted
   * state so autosave never writes a list's options into the schema.
   */
  resolvedLists: Record<string, ResolvedList>;
  /** Codes this schema has issued, for the composer's edit warning. */
  mintedCount?: number;
  /**
   * Validate findings anchored to a single cell, keyed by {@link cellKey}.
   *
   * Deliberately fed into the SAME `violation` channel the live
   * `validationPattern` check already uses — one red tint, one triangle, one
   * tooltip. A second visual language for "this cell is wrong" would leave the
   * grid with two reds meaning different things.
   */
  cellProblems?: Map<string, SchemaProblem[]>;
  /** Validate findings anchored to a whole column, keyed by field id. */
  fieldProblems?: Map<string, SchemaProblem[]>;
  /**
   * A cell or column the details popover wants revealed.
   *
   * Needed because the grid windows past 50 rows: a finding below the fold has
   * no DOM node to hover, so the summary used to tell people to hover something
   * that was not there. Routed by the field's group — each group table owns its
   * own scroller and selection.
   */
  focusTarget?: FocusTarget | null;
}

/**
 * Spreadsheet grid editor for a schema's fields (SME: left-to-right table).
 * Media fields and Content fields render as two separate, accent-topped tables
 * sitting side-by-side with a small gap — Content to the RIGHT of Media, not
 * below. Their rows stay aligned because both are driven by one shared row
 * count; a single "Add row" appends across every field in both groups.
 *
 * Pure controlled component: every edit produces a new `fields` array and calls
 * `onChange`; the parent owns persistence (debounced autosave).
 */
export function FieldsEditor({
  fields,
  readOnly,
  onChange,
  taxonomyId,
  orgWide,
  resolvedLists,
  mintedCount,
  cellProblems,
  fieldProblems,
  focusTarget,
  onEditingChange,
}: FieldsEditorProps) {
  const { t } = useTranslation('knowledge');
  const openEditorsRef = useRef(new Set<string>());
  const reportEditing = (key: string, open: boolean) => {
    const openEditors = openEditorsRef.current;
    const wasOpen = openEditors.size > 0;
    if (open) openEditors.add(key);
    else openEditors.delete(key);
    if (openEditors.size > 0 !== wasOpen) onEditingChange?.(!wasOpen);
  };
  const [addGroup, setAddGroup] = useState<TaxonomyFieldGroup | null>(null);
  const [addAfterId, setAddAfterId] = useState<string | null>(null);
  /** When adding a SUBFIELD, the parent field's id (locks the group + "-" join). */
  const [addParentId, setAddParentId] = useState<string | null>(null);
  /** Contextual adds (subfield / add-to-right) skip the library picker. */
  const [addSkipPicker, setAddSkipPicker] = useState(false);
  const [editingField, setEditingField] = useState<TaxonomyField | null>(null);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  /** Id of the computed field whose pattern composer is open. */
  const [composerFieldId, setComposerFieldId] = useState<string | null>(null);
  /** Id of the linked field whose target composer is open. */
  const [linkedFieldId, setLinkedFieldId] = useState<string | null>(null);
  /** Id of the library field pending a "Reset to default" confirmation. */
  const [resetFieldId, setResetFieldId] = useState<string | null>(null);
  /** Id of the field whose shared-list binding is being configured. */
  const [linkListFieldId, setLinkListFieldId] = useState<string | null>(null);

  // Schema names for linked-field subtitles (id → name). Read-only lookup.
  const { data: schemaList = [] } = useQuery(taxonomiesQueryOptions());
  const linkedSchemaNames: Record<string, string> = Object.fromEntries(
    schemaList.map((s) => [s.id, s.name]),
  );

  const mediaFields = fields.filter((f) => f.group === 'media');
  const contentFields = fields.filter((f) => f.group === 'content');
  const hasAnyField = mediaFields.length > 0 || contentFields.length > 0;

  // Computed in the browser, never minted: a mint spends one of the schema's permanent codes.
  const handleGenerateSample = (variable: TaxonomyField) => {
    const code = sampleTrackingCode(variable, hydratedFields);
    if (code === '') {
      enqueueSnackbar(t('taxonomy.schemaSettings.sampleEmpty'), { variant: 'info' });
      return;
    }
    enqueueSnackbar(t('taxonomy.fieldsEditor.sampleToast', { code }), { variant: 'success' });
  };

  /**
   * How many rows the grid renders.
   *
   * A list-bound column draws its cells from the RESOLVED list, not from its
   * inline `values`, so its length has to come from the same place. Measuring
   * only `f.values.length` truncated bound columns to their inline FALLBACK —
   * Language showed ~6 of its 183 entries, and adding rows to some other field
   * made the missing languages "appear" as the row count grew past them. The
   * options were always there; the grid just wasn't tall enough to show them.
   */
  const rowsInField = (f: TaxonomyField) =>
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- resolvedLists only holds entries for list-bound fields
    f.listRef?.listId ? (resolvedLists[f.id]?.values.length ?? f.values.length) : f.values.length;

  const maxLen = fields.reduce((m, f) => Math.max(m, rowsInField(f)), 0);

  /**
   * The same fields with bound columns' vocabularies filled in.
   *
   * A Variable's preview reads its referenced field's `values` row by row, and
   * a bound column's own `values` are the stale inline fallback — so building
   * the preview from the raw fields showed codes for the few inline rows and
   * blanks for every row the shared list actually supplies. The grid already
   * resolves bound columns for its own cells; this makes the Variable read the
   * same vocabulary rather than a different one.
   */
  const hydratedFields = useMemo(
    () => substituteResolvedValues(fields, resolvedLists),
    [fields, resolvedLists],
  );

  // ─── Row virtualization ───────────────────────────────────────────────────
  // Both group tables share this scroller and the same row count, so a single
  // virtualizer drives both — they render identical spacer/visible windows and
  // stay row-aligned. Only kicks in past a threshold; smaller schemas render
  // every row exactly as before (windowing adds nothing there). Fixed row
  // height (no measureElement) keeps the two tables in lockstep.
  const ROW_HEIGHT = 37; // h-9 cell (36px) + 1px bottom border
  const VIRTUALIZE_THRESHOLD = 50;
  const shouldVirtualize = maxLen > VIRTUALIZE_THRESHOLD;
  const boardScrollRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  // Rows sit below the sticky <thead>; scrollMargin = that offset so the
  // virtualizer's row coordinates line up with the scroller. Measured off the
  // first tbody; both tables' bodies start at the same Y.
  useEffect(() => {
    const scroller = boardScrollRef.current;
    if (!scroller || !shouldVirtualize) return;
    const measure = () => {
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- `tbody` is a current tag; the rule cannot pin the non-deprecated overload
      const tbody = scroller.querySelector<HTMLTableSectionElement>('tbody');
      if (!tbody) return;
      const next =
        tbody.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop;
      setScrollMargin((prev) => (Math.abs(prev - next) > 0.5 ? next : prev));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(scroller);
    return () => ro.disconnect();
  }, [shouldVirtualize, fields.length, maxLen]);

  const rowVirtualizer = useVirtualizer({
    count: maxLen,
    getScrollElement: () => boardScrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
    scrollMargin,
    enabled: shouldVirtualize,
  });

  // The windowed slice + spacer heights, shared by both group tables (null when
  // not virtualizing → tables render all rows).
  const virtualItems = rowVirtualizer.getVirtualItems();
  const rowWindow =
    shouldVirtualize && virtualItems.length > 0
      ? {
          indexes: virtualItems.map((v) => v.index),
          paddingTop: Math.max(0, virtualItems[0].start - scrollMargin),
          paddingBottom: Math.max(
            0,
            rowVirtualizer.getTotalSize() -
              (virtualItems[virtualItems.length - 1].end - scrollMargin),
          ),
        }
      : null;

  /**
   * Scrolls a row the window never rendered into view.
   *
   * Only reachable for an off-window row, because a mounted cell is scrolled by
   * the DOM directly (see `revealCell` in GroupTable) — which also handles the
   * horizontal axis the virtualizer knows nothing about. `scrollToIndex` is a
   * hard no-op while `enabled` is false: with no measurements there is no
   * offset to compute, so the DOM path is the only one that works below the
   * threshold, not merely the cheaper one.
   */
  const scrollRowIntoView = (row: number) => {
    if (shouldVirtualize) rowVirtualizer.scrollToIndex(row, { align: 'auto' });
  };

  /** Delete a field AND (if it's a parent) its subfields — one level deep. */
  const removeField = (id: string) =>
    onChange(fields.filter((f) => f.id !== id && f.parentId !== id));
  const patchField = (id: string, patch: Partial<TaxonomyField>, opts?: { immediate?: boolean }) =>
    onChange(applyFieldPatch(fields, id, patch), opts);

  /** Id of the last field in `id`'s cluster (the field + any of its subfields,
   *  which are kept adjacent right after it). For a subfield, returns itself. */
  const clusterEndId = (id: string): string => {
    const start = fields.findIndex((f) => f.id === id);
    if (start === -1) return id;
    let end = start;
    for (let j = start + 1; j < fields.length; j++) {
      if (fields[j].parentId === id) end = j;
      else break;
    }
    return fields[end].id;
  };

  /** Insert `newField` right after `afterId` in the flat field order. */
  const insertAfter = (afterId: string, newField: TaxonomyField) => {
    const idx = fields.findIndex((f) => f.id === afterId);
    if (idx === -1) {
      onChange([...fields, newField]);
      return;
    }
    const next = [...fields];
    next.splice(idx + 1, 0, newField);
    onChange(next);
  };

  const handleAdd = (field: TaxonomyField) => {
    const nf = addParentId ? { ...field, parentId: addParentId } : field;
    if (addAfterId) insertAfter(addAfterId, nf);
    else onChange([...fields, nf]);
  };

  const duplicateField = (field: TaxonomyField) =>
    insertAfter(clusterEndId(field.id), {
      ...field,
      id: crypto.randomUUID(),
      name: t('taxonomy.fieldsEditor.duplicateName', { name: field.name }),
      parentId: field.parentId ?? null,
      libraryKey: field.libraryKey ?? null,
      values: field.values.map((v) => ({ ...v })),
    });

  /**
   * Unbinds a field from its shared list. The inline `values` were never
   * overwritten while bound, so the column simply returns to whatever was
   * typed before — no data recovery needed.
   */
  const unlinkFromList = (id: string) => patchField(id, { listRef: undefined }, IMMEDIATE);

  /** Restore a library field's values/format to its catalog defaults (destructive). */
  const resetFieldToDefault = (id: string) => {
    const field = fields.find((f) => f.id === id);
    const def = getStandardFieldByKey(field?.libraryKey);
    if (!field || !def) return;
    patchField(
      id,
      { values: def.values.map((v) => ({ ...v })), format: def.format ?? null },
      IMMEDIATE,
    );
    setResetFieldId(null);
  };

  /** ⋮ "Add field to the right" — a same-level sibling of `field` (opens the library picker). */
  const addSiblingAfter = (field: TaxonomyField) => {
    setAddParentId(field.parentId ?? null);
    // subfield sibling → right after it; top-level → after its whole cluster
    setAddAfterId(field.parentId ? field.id : clusterEndId(field.id));
    setAddGroup(field.group);
    setAddSkipPicker(false);
  };

  /** ⋮ "Add subfield" — a child of `field`, appended after its existing subfields (library picker). */
  const addSubfield = (field: TaxonomyField) => {
    setAddParentId(field.id);
    setAddAfterId(clusterEndId(field.id));
    setAddGroup(field.group);
    setAddSkipPicker(false);
  };

  /**
   * ⋮ "Detach from parent" — promotes a subfield to a field of its own.
   *
   * The way out of a cluster. A subfield's section is locked to its parent and
   * its name joins with the subfield delimiter, so without this the only escape
   * was deleting it and building it again. It keeps its group and stays where it
   * sits, so it lands immediately after the cluster it left — no reshuffle, and
   * the "_" it now joins with is visible in the preview straight away.
   */
  const detachField = (id: string) => {
    onChange(fields.map((f) => (f.id === id ? { ...f, parentId: null } : f)));
  };

  const closeAddModal = () => {
    setAddGroup(null);
    setAddAfterId(null);
    setAddParentId(null);
    setAddSkipPicker(false);
  };

  const setCell = (fieldId: string, row: number, patch: Partial<TaxonomyFieldValue>) =>
    onChange(
      pruneEmptyRows(
        fields.map((f) => {
          if (f.id !== fieldId) return f;
          const values = [...f.values];
          while (values.length <= row) values.push({ ...EMPTY_VALUE });
          values[row] = { ...values[row], ...patch };
          return { ...f, values };
        }),
      ),
    );

  /** Apply MANY cell writes in a single update (copy/paste, fill, clear). Doing
   *  these as N separate setCell calls loses all but the last (each derives from
   *  the same stale `fields`), so batch them here. */
  const setCells = (
    writes: { fieldId: string; row: number; sub: 'value' | 'code'; value: string }[],
  ) => {
    if (writes.length === 0) return;
    onChange(
      pruneEmptyRows(
        fields.map((f) => {
          const fw = writes.filter((w) => w.fieldId === f.id);
          if (fw.length === 0) return f;
          const values = f.values.map((v) => ({ ...v }));
          for (const w of fw) {
            while (values.length <= w.row) values.push({ ...EMPTY_VALUE });
            values[w.row] = { ...values[w.row], [w.sub]: w.value };
          }
          return { ...f, values };
        }),
      ),
      { immediate: true },
    );
  };

  /** Reorder columns via drag. Moves the dragged field to the drop target, then
   *  normalizes so subfields stay adjacent to their parent (a top-level drag
   *  carries its cluster; a subfield can only reorder within its parent). */
  const reorderFields = (activeId: string, overId: string) => {
    const from = fields.findIndex((f) => f.id === activeId);
    const to = fields.findIndex((f) => f.id === overId);
    if (from < 0 || to < 0 || from === to) return;
    const moved = arrayMove(fields, from, to);
    // Re-group: each top-level field (in the moved order) followed by its subfields.
    const result: TaxonomyField[] = [];
    const placed = new Set<string>();
    for (const f of moved) {
      if (f.parentId) continue;
      result.push(f);
      placed.add(f.id);
      for (const sub of moved.filter((s) => s.parentId === f.id)) {
        result.push(sub);
        placed.add(sub.id);
      }
    }
    // Safety: append any orphaned subfields (parent missing) so none are lost.
    for (const f of moved) if (!placed.has(f.id)) result.push(f);
    onChange(result);
  };

  /** Append one empty row across EVERY field (a spreadsheet row add). */
  const addRow = () =>
    onChange(
      fields.map((f) => ({ ...f, values: [...f.values, { ...EMPTY_VALUE }] })),
      { immediate: true },
    );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {/* Media on the left, Content to the right; each group's label sits above
          its own columns and its "Add field" is pinned to that column's right.
          This area is the row scroller: the parent card pins full-height, so the
          rows scroll here (headers stay via the sticky <thead>) and the
          horizontal scrollbar sits at the bottom edge of the card. */}
      <div
        ref={boardScrollRef}
        className="flex min-h-0 flex-1 items-start gap-4 overflow-auto pb-1"
      >
        {(['media', 'content'] as TaxonomyFieldGroup[]).map((group) => {
          const gFields = group === 'media' ? mediaFields : contentFields;
          const meta = GROUP_META[group];
          const openAdd = () => {
            setAddAfterId(null);
            setAddParentId(null);
            setAddSkipPicker(false);
            setAddGroup(group);
          };

          if (gFields.length === 0) {
            return (
              <div key={group} className="flex shrink-0 flex-col gap-2">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <span className={`size-2 shrink-0 rounded-full ${meta.dot}`} aria-hidden />
                    <h3 className="whitespace-nowrap text-sm font-semibold text-foreground">
                      {meta.label}
                    </h3>
                    <Badge variant="ghost" roundness="full" className="border-border-light text-xs">
                      0
                    </Badge>
                  </div>
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="mini"
                      className="h-7 shrink-0 whitespace-nowrap text-xs text-muted-foreground"
                      onClick={openAdd}
                    >
                      <Plus className="size-3.5" />
                      {t('taxonomy.fieldsEditor.addField')}
                    </Button>
                  )}
                </div>
                <div
                  className="flex items-center justify-center rounded-lg border border-dashed border-border-light px-6 py-8 text-center text-sm text-muted-foreground"
                  style={{ minWidth: MIN_GROUP_WIDTH }}
                >
                  {meta.emptyLabel}
                </div>
              </div>
            );
          }

          return (
            <GroupTable
              key={group}
              group={group}
              fields={gFields}
              allFields={hydratedFields}
              linkedSchemaNames={linkedSchemaNames}
              rowCount={maxLen}
              rowWindow={rowWindow}
              onScrollRowIntoView={scrollRowIntoView}
              /*
               * Only the table holding the field acts on it. Each group owns its
               * own selection and scroller, so handing the target to both would
               * move two focus rings and fight over the scroll position.
               */
              focusTarget={
                focusTarget && gFields.some((f) => f.id === focusTarget.fieldId)
                  ? focusTarget
                  : null
              }
              readOnly={readOnly}
              hoveredRow={hoveredRow}
              onHoverRow={setHoveredRow}
              onSetCell={setCell}
              onSetCells={setCells}
              onAddField={openAdd}
              onEditField={setEditingField}
              onConfigureField={(f) =>
                f.type === 'linked' ? setLinkedFieldId(f.id) : setComposerFieldId(f.id)
              }
              onResetField={(f) => setResetFieldId(f.id)}
              onLinkList={(f) => setLinkListFieldId(f.id)}
              onUnlinkList={(f) => unlinkFromList(f.id)}
              resolvedLists={resolvedLists}
              cellProblems={cellProblems}
              fieldProblems={fieldProblems}
              onGenerateSample={handleGenerateSample}
              onAddFieldAfter={addSiblingAfter}
              onAddSubfield={addSubfield}
              onDetachField={detachField}
              onDuplicateField={duplicateField}
              onRemoveField={removeField}
              onResize={patchField}
              onReorder={reorderFields}
              onRename={(id, name) => patchField(id, { name })}
              onEditingChange={(key, open) => reportEditing(`${group}:${key}`, open)}
            />
          );
        })}
      </div>

      {hasAnyField && !readOnly && (
        <Button
          variant="outline"
          size="mini"
          className="h-8 w-fit justify-start text-xs text-muted-foreground"
          onClick={addRow}
        >
          <Plus className="size-3.5" />
          {t('taxonomy.fieldsEditor.addRow')}
        </Button>
      )}

      {composerFieldId &&
        (() => {
          const cf = fields.find((f) => f.id === composerFieldId);
          if (!cf) return null;
          return (
            <TrackingCodeComposer
              open
              field={cf}
              fields={fields}
              mintedCount={mintedCount}
              onClose={() => setComposerFieldId(null)}
              onSave={({ name, pattern }) => {
                patchField(cf.id, { name, pattern }, IMMEDIATE);
                setComposerFieldId(null);
              }}
            />
          );
        })()}

      {linkedFieldId &&
        (() => {
          const lf = fields.find((f) => f.id === linkedFieldId);
          if (!lf) return null;
          return (
            <LinkedSchemaComposer
              open
              field={lf}
              currentSchemaId={taxonomyId}
              onClose={() => setLinkedFieldId(null)}
              onSave={(link) => {
                patchField(lf.id, { link }, IMMEDIATE);
                setLinkedFieldId(null);
              }}
            />
          );
        })()}

      {linkListFieldId &&
        (() => {
          const lf = fields.find((f) => f.id === linkListFieldId);
          if (!lf) return null;
          return (
            <LinkListModal
              open
              field={lf}
              onClose={() => setLinkListFieldId(null)}
              onSave={(listRef) => patchField(lf.id, { listRef }, IMMEDIATE)}
              orgWide={orgWide}
            />
          );
        })()}

      {resetFieldId &&
        (() => {
          const rf = fields.find((f) => f.id === resetFieldId);
          const def = getStandardFieldByKey(rf?.libraryKey);
          if (!rf || !def) return null;
          return (
            <FormDialog
              open
              onOpenChange={(next) => !next && setResetFieldId(null)}
              title={t('taxonomy.fieldsEditor.resetToDefault', { name: rf.name })}
              className="flex flex-col sm:max-w-[28rem]"
            >
              <FormDialog.Body>
                <p className="text-sm text-muted-foreground">
                  <Trans
                    i18nKey="taxonomy.fieldsEditor.resetReplacesValues"
                    ns="knowledge"
                    count={def.values.length}
                    values={{ name: rf.name }}
                    components={{ strong: <strong /> }}
                  />
                </p>
              </FormDialog.Body>
              <FormDialog.Footer
                primary={{
                  label: t('taxonomy.fieldsEditor.resetDefault'),
                  onClick: () => resetFieldToDefault(rf.id),
                }}
                secondary={{
                  label: t('taxonomy.fieldsEditor.cancel'),
                  onClick: () => setResetFieldId(null),
                }}
              />
            </FormDialog>
          );
        })()}

      {addGroup && (
        <AddFieldModal
          open
          defaultGroup={addGroup}
          existingFields={fields}
          lockGroup={addParentId !== null}
          isSubfield={addParentId !== null}
          skipPicker={addSkipPicker}
          onClose={closeAddModal}
          onAdd={handleAdd}
          orgWide={orgWide}
        />
      )}
      {editingField && (
        <EditFieldModal
          open
          field={editingField}
          parentName={fields.find((f) => f.id === editingField.parentId)?.name}
          orgWide={orgWide}
          onClose={() => setEditingField(null)}
          onSave={(patch) => {
            patchField(editingField.id, patch, IMMEDIATE);
            setEditingField(null);
          }}
        />
      )}
    </div>
  );
}

FieldsEditor.displayName = 'FieldsEditor';
